'use client';

import { motion } from 'framer-motion';
import type {
  ChurchEvent,
  RecurringSeries,
  StandaloneOpportunity,
  SermonSeries,
  Announcement,
  BaptismInfo,
  LifeGroupsInfo,
  ImNewInfo,
} from '@/types/serve-finder';
import { EventCard } from './EventCard';
import { SeriesCard } from './SeriesCard';
import { ServingGroupCard } from './StandaloneCard';
import { CurrentSeriesCard } from './CurrentSeriesCard';
import { AnnouncementCard } from './AnnouncementCard';
import { BaptismCard } from './BaptismCard';
import { GroupsCard } from './GroupsCard';
import { ImNewCard } from './ImNewCard';
import { SkeletonLoader } from './SkeletonLoader';
import { FeaturedEventSection } from './FeaturedEventSection';

interface EventGridProps {
  events: ChurchEvent[];
  series: RecurringSeries[];
  standaloneOpps: StandaloneOpportunity[];
  sermonSeries: SermonSeries | null;
  announcements: Announcement[];
  baptism: BaptismInfo | null;
  lifeGroups: LifeGroupsInfo | null;
  imNew: ImNewInfo | null;
  isLoading: boolean;
  featured?: boolean;
}

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
  },
};

export function EventGrid({
  events,
  series,
  standaloneOpps,
  sermonSeries,
  announcements,
  baptism,
  lifeGroups,
  imNew,
  isLoading,
  featured,
}: EventGridProps) {
  if (isLoading) {
    return <SkeletonLoader />;
  }

  const totalItems =
    events.length +
    series.length +
    announcements.length +
    (standaloneOpps.length > 0 ? 1 : 0) +
    (sermonSeries ? 1 : 0) +
    (baptism ? 1 : 0) +
    (lifeGroups ? 1 : 0) +
    (imNew ? 1 : 0);

  if (totalItems === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-foreground text-lg font-semibold">Nothing coming up right now</p>
        <p className="text-muted-foreground mt-1 text-sm">
          Check back soon — new events and serving opportunities are added regularly.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Featured sections — desktop only */}
      {featured && (
        <div className="relative z-10 mb-8 hidden space-y-8 lg:block">
          {events[0] && <FeaturedEventSection event={events[0]} />}
        </div>
      )}

      {/* Normal grid */}
      <motion.div
        className="grid grid-cols-1 gap-x-5 sm:grid-cols-2 lg:grid-cols-3"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* First event (Hope 365) — render before sermon series so it appears first */}
        {events[0] && (
          <motion.div
            key={`event-${events[0].ID}`}
            className={`row-span-3 mb-5 grid grid-rows-[subgrid] ${featured ? 'lg:hidden' : ''}`}
            variants={itemVariants}
          >
            <EventCard event={events[0]} />
          </motion.div>
        )}

        {/* Current Sermon Series */}
        {sermonSeries && (
          <motion.div
            key={`sermon-${sermonSeries.ID}`}
            className="row-span-3 mb-5 grid grid-rows-[subgrid]"
            variants={itemVariants}
          >
            <CurrentSeriesCard series={sermonSeries} />
          </motion.div>
        )}

        {/* Life Groups */}
        {lifeGroups && (
          <motion.div
            key="life-groups"
            className="row-span-3 mb-5 grid grid-rows-[subgrid]"
            variants={itemVariants}
          >
            <GroupsCard lifeGroups={lifeGroups} />
          </motion.div>
        )}

        {/* Remaining Events */}
        {events.slice(1).map((event) => (
          <motion.div
            key={`event-${event.ID}`}
            className="row-span-3 mb-5 grid grid-rows-[subgrid]"
            variants={itemVariants}
          >
            <EventCard event={event} />
          </motion.div>
        ))}

        {/* Baptism */}
        {baptism && (
          <motion.div
            key="baptism"
            className="row-span-3 mb-5 grid grid-rows-[subgrid]"
            variants={itemVariants}
          >
            <BaptismCard baptism={baptism} />
          </motion.div>
        )}

        {/* I'm New */}
        {imNew && (
          <motion.div
            key="im-new"
            className="row-span-3 mb-5 grid grid-rows-[subgrid]"
            variants={itemVariants}
          >
            <ImNewCard imNew={imNew} />
          </motion.div>
        )}

        {/* Announcements */}
        {announcements.map((a) => (
          <motion.div
            key={`announce-${a.ID}`}
            className="row-span-3 mb-5 grid grid-rows-[subgrid]"
            variants={itemVariants}
          >
            <AnnouncementCard announcement={a} />
          </motion.div>
        ))}

        {/* Recurring Series */}
        {series.map((s) => (
          <motion.div
            key={`series-${s.ID}`}
            className="row-span-3 mb-5 grid grid-rows-[subgrid]"
            variants={itemVariants}
          >
            <SeriesCard series={s} />
          </motion.div>
        ))}

        {/* Ongoing Serving */}
        {standaloneOpps.length > 0 && (
          <motion.div
            key="serving-group"
            className="row-span-3 mb-5 grid grid-rows-[subgrid]"
            variants={itemVariants}
          >
            <ServingGroupCard opportunities={standaloneOpps} />
          </motion.div>
        )}
      </motion.div>
    </>
  );
}
